import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { Toaster } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  XCircle,
  Play,
  Users,
  DollarSign,
  Clock,
  Target,
  ChevronRight,
  Briefcase,
  MessageSquare,
  Shield,
  Zap
} from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2211773d-f0b3-4564-83a5-4cc7f60a1377/artifacts/bzxne9u9_6744c91694f9840615c75764.png";

// Scroll to questionnaire function
const scrollToQuestionnaire = () => {
  document.getElementById('questionnaire')?.scrollIntoView({ behavior: 'smooth' });
};

// Navigation Component
const ApplyNavigation = () => {
  return (
    <nav className="bg-[#0B1120] py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <div className="flex items-center">
          <img src={LOGO_URL} alt="Aspire Partners" className="h-10" />
        </div>
        <div></div>
      </div>
    </nav>
  );
};

// Hero Section with Video
const ApplyHero = () => {
  return (
    <section className="bg-gradient-to-br from-[#0B1120] to-[#0F172A] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[#0099CC] font-semibold text-sm uppercase tracking-wider mb-4">
              Partner Application
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Turn Your Network Into{" "}
              <span className="text-[#0099CC]">Monthly Passive Income</span>
            </h1>
            <p className="text-xl text-slate-300 mb-4">
              You Already Know the Right People.
            </p>
            <p className="text-2xl font-semibold text-white mb-8">
              Now It's Time to Get Paid for It.
            </p>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <p className="text-slate-400 mb-4">
                Most professionals make introductions every day…
                <br />
                <strong className="text-white">But never earn a dollar from them.</strong>
              </p>
              <p className="text-[#0099CC] font-semibold text-lg mb-4">
                Aspire Partners changes that.
              </p>
              
              {/* Risk-Free Guarantee Badge */}
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-400 font-bold text-sm">100% RISK-FREE</p>
                    <p className="text-white font-semibold">30-Day Money-Back Guarantee</p>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={scrollToQuestionnaire}
                className="w-full bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-xl py-4 text-lg font-semibold"
              >
                See If You Qualify
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>

          {/* Right - Video */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <Play className="w-5 h-5 text-[#0099CC]" />
                <span className="text-white font-semibold">Watch This Before You Apply</span>
              </div>
              <div className="rounded-xl overflow-hidden">
                <video
                  controls
                  className="w-full aspect-video"
                  poster=""
                >
                  <source src="https://customer-assets.emergentagent.com/job_intro-income/artifacts/elq7755p_Aspire%202.mov" type="video/quicktime" />
                  <source src="https://customer-assets.emergentagent.com/job_intro-income/artifacts/elq7755p_Aspire%202.mov" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <p className="text-slate-400 text-sm mt-4">
                Discover how professionals are earning <strong className="text-white">$2K–$50K+ per month</strong> in recurring income
                just by connecting businesses to solutions they already need.
              </p>
              <div className="flex flex-wrap gap-4 mt-4">
                <span className="text-sm text-slate-300 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> No selling
                </span>
                <span className="text-sm text-slate-300 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> No fulfillment
                </span>
                <span className="text-sm text-slate-300 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> No overhead
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Why This Exists Section
const WhySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-16 md:py-24 bg-white" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <p className="text-[#0099CC] font-semibold text-sm uppercase tracking-wider mb-4">
            Why This Exists
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-8">
            The Opportunity Most People Miss
          </h2>
          
          <div className="text-left bg-slate-50 rounded-2xl p-8 mb-8">
            <p className="text-lg text-slate-700 mb-6">Every day, you:</p>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <MessageSquare className="w-8 h-8 text-[#0099CC] mb-2" />
                <p className="font-semibold text-[#0F172A]">Recommend tools</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <Users className="w-8 h-8 text-[#0099CC] mb-2" />
                <p className="font-semibold text-[#0F172A]">Refer services</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <Target className="w-8 h-8 text-[#0099CC] mb-2" />
                <p className="font-semibold text-[#0F172A]">Connect people</p>
              </div>
            </div>
            
            <p className="text-lg text-slate-700 mb-4">But here's the problem…</p>
            <p className="text-xl font-bold text-[#0F172A] mb-6">
              👉 You're not getting paid for it.
            </p>
            <p className="text-slate-600">
              Meanwhile, companies are spending millions trying to acquire the exact same relationships you already have.
            </p>
          </div>
          
          <p className="text-2xl font-bold text-[#0099CC]">
            Aspire Partners flips the model.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// What Aspire Is Section
const WhatAspireSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const solutions = [
    "Cost reduction (technology, healthcare, expenses)",
    "AI and operational efficiency tools",
    "Revenue and cash flow optimization",
    "Risk, compliance, and business services"
  ];

  return (
    <section className="py-16 md:py-24 bg-slate-50" ref={ref}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <p className="text-[#0099CC] font-semibold text-sm uppercase tracking-wider mb-4">
            What Aspire Partners Is
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
            A Smarter Way to Monetize Your Network
          </h2>
          <p className="text-lg text-slate-600">
            Aspire Partners connects businesses to high-demand solutions like:
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution}
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200"
            >
              <CheckCircle2 className="w-6 h-6 text-[#0099CC] flex-shrink-0" />
              <span className="text-slate-700 font-medium">{solution}</span>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-[#0099CC]/10 to-[#0099CC]/5 rounded-2xl p-6 text-center"
        >
          <p className="text-lg">
            💡 These are not "nice-to-have" services
          </p>
          <p className="text-xl font-bold text-[#0F172A]">
            They are urgent, high-value business needs
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksApply = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    {
      number: "1",
      title: "You Identify Opportunities",
      description: "Businesses in your network that could benefit",
      icon: <Target className="w-6 h-6" />
    },
    {
      number: "2",
      title: "You Make the Introduction",
      description: "Warm intro, email, or simple connection",
      icon: <Users className="w-6 h-6" />
    },
    {
      number: "3",
      title: "We Handle Everything",
      description: "Sales, closing, onboarding, fulfillment",
      icon: <Briefcase className="w-6 h-6" />
    },
    {
      number: "4",
      title: "You Get Paid Monthly",
      description: "Recurring revenue for as long as the client stays",
      icon: <DollarSign className="w-6 h-6" />
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white" ref={ref}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <p className="text-[#0099CC] font-semibold text-sm uppercase tracking-wider mb-4">
            How It Works
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-2">
            Simple. Scalable. Recurring.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15 }}
              className="relative"
            >
              <div className="bg-slate-50 rounded-2xl p-6 text-center h-full border border-slate-100 hover:border-[#0099CC]/30 transition-colors">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#0099CC] text-white flex items-center justify-center font-bold text-xl">
                  {step.number}
                </div>
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-[#0099CC]/10 text-[#0099CC] flex items-center justify-center">
                  {step.icon}
                </div>
                <h3 className="font-bold text-[#0F172A] mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-slate-300" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center mt-10"
        >
          <Button
            onClick={scrollToQuestionnaire}
            className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-xl py-4 px-8 text-lg font-semibold"
          >
            See If You Qualify
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Why People Join Section
const WhyJoinSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const benefits = [
    "Earn recurring revenue (not one-time commissions)",
    "No sales experience required",
    "No product delivery or support",
    "No inventory, employees, or overhead",
    "Leverage relationships you already have",
    "Ongoing training and support"
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-[#0B1120] to-[#0F172A]" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <p className="text-[#0099CC] font-semibold text-sm uppercase tracking-wider mb-4">
            Why People Join Aspire Partners
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            This Isn't Another Side Hustle
          </h2>
          <p className="text-xl text-[#0099CC] mb-12">
            This is a long-term income engine
          </p>

          <div className="grid sm:grid-cols-2 gap-4 text-left">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10"
              >
                <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                <span className="text-white">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Income Potential Section
const IncomePotentialSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-16 md:py-24 bg-white" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <p className="text-[#0099CC] font-semibold text-sm uppercase tracking-wider mb-4">
            Income Potential
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
            What Your Network Could Be Worth
          </h2>
          <p className="text-slate-600 mb-12">
            Most partners underestimate this… Even a small number of introductions can turn into:
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { amount: "$5K", label: "/month" },
              { amount: "$10K", label: "/month" },
              { amount: "$25K+", label: "/month" }
            ].map((item, index) => (
              <motion.div
                key={item.amount}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: index * 0.15 }}
                className="bg-gradient-to-br from-slate-50 to-white p-8 rounded-2xl border border-slate-200 shadow-lg"
              >
                <p className="text-4xl md:text-5xl font-bold text-[#0099CC] font-numbers">
                  {item.amount}
                </p>
                <p className="text-slate-500">{item.label}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-slate-600 mb-4">And it compounds over time.</p>
          <div className="bg-[#0099CC]/10 rounded-xl p-6 inline-block mb-8">
            <p className="text-lg">
              💡 Some partners are building <strong className="text-[#0099CC]">six-figure recurring income streams</strong>
              <br />from connections they already had.
            </p>
          </div>

          {/* CTA Button */}
          <div className="block">
            <Button
              onClick={scrollToQuestionnaire}
              className="bg-[#0099CC] hover:bg-[#007EA8] text-white rounded-xl py-4 px-8 text-lg font-semibold"
            >
              See If You Qualify
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Who This Is For Section
const QualificationSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const forYou = [
    "You have a strong professional network",
    "You know business owners, executives, or decision-makers",
    "You naturally connect people and opportunities",
    "You want income that scales without trading time for money"
  ];

  const notForYou = [
    "You're looking for a \"get rich quick\" scheme",
    "You don't have access to business decision-makers",
    "You're not willing to make introductions",
    "You want guaranteed income without effort"
  ];

  return (
    <section className="py-16 md:py-24 bg-slate-50" ref={ref}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* For You */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            className="bg-white rounded-2xl p-8 border border-slate-200"
          >
            <h3 className="text-2xl font-bold text-[#0F172A] mb-6">
              This Is For You If:
            </h3>
            <div className="space-y-3">
              {forYou.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Not For You */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8 border border-slate-200"
          >
            <h3 className="text-2xl font-bold text-[#0F172A] mb-6">
              This Is NOT For You If:
            </h3>
            <div className="space-y-3">
              {notForYou.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Urgency Section
const UrgencySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-[#0B1120] to-[#0F172A]" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <Clock className="w-12 h-12 text-[#0099CC] mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Why You Should Apply Now
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            The biggest mistake people make is waiting.
          </p>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-left mb-8">
            <p className="text-slate-300 mb-4">While you're thinking about it:</p>
            <div className="space-y-3">
              <p className="text-white flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-[#0099CC]" />
                Others are monetizing their networks
              </p>
              <p className="text-white flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-[#0099CC]" />
                Deals are being closed
              </p>
              <p className="text-white flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-[#0099CC]" />
                Residual income is stacking
              </p>
            </div>
          </div>

          <p className="text-xl text-[#0099CC] font-semibold">
            👉 The earlier you start, the bigger your income base becomes.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// GHL Embedded Survey Form Component
const QuestionnaireForm = () => {
  const ref = useRef(null);

  useEffect(() => {
    // Load GHL form embed script
    const script = document.createElement('script');
    script.src = 'https://go.aspirepartnersusa.com/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://go.aspirepartnersusa.com/js/form_embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="bg-white rounded-3xl p-4 md:p-8 shadow-2xl" ref={ref}>
      <iframe 
        src="https://go.aspirepartnersusa.com/widget/survey/Zpsh1qR4YUgIerH3PMLL" 
        style={{ border: 'none', width: '100%', minHeight: '700px' }}
        scrolling="no" 
        id="Zpsh1qR4YUgIerH3PMLL" 
        title="Aspire Partners Application Survey"
        data-testid="ghl-survey-iframe"
      />
    </div>
  );
};
// Form Section
const FormSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="questionnaire" className="py-16 md:py-24 bg-white" ref={ref}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0099CC]/10 rounded-full mb-6">
            <Zap className="w-4 h-4 text-[#0099CC]" />
            <span className="text-sm font-medium text-[#0099CC]">Takes less than 2 minutes</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
            See If You Qualify
          </h2>
          <p className="text-slate-600">
            Complete the quick questionnaire below to:
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            <span className="text-sm text-slate-700 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> See your income potential
            </span>
            <span className="text-sm text-slate-700 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Find out if Aspire Partners is the right fit
            </span>
            <span className="text-sm text-slate-700 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Unlock access to the full partner opportunity
            </span>
          </div>
        </motion.div>

        <QuestionnaireForm />
      </div>
    </section>
  );
};

// Footer
const ApplyFooter = () => {
  return (
    <footer className="bg-[#0B1120] py-12 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src={LOGO_URL} alt="Aspire Partners" className="h-10" />
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Aspire Partners. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-slate-400 hover:text-[#0099CC] text-sm transition-colors">
              Privacy Policy
            </a>
            <Link to="/terms-and-conditions" className="text-slate-400 hover:text-[#0099CC] text-sm transition-colors">
              Terms & Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Apply Page Component
const ApplyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" richColors />
      <ApplyNavigation />
      <ApplyHero />
      <WhySection />
      <WhatAspireSection />
      <HowItWorksApply />
      <WhyJoinSection />
      <IncomePotentialSection />
      <QualificationSection />
      <UrgencySection />
      <FormSection />
      <ApplyFooter />
    </div>
  );
};

export default ApplyPage;
